// AdminMessagePopup — shown to a school user the FIRST time they log in
// after admin sends them a new inbox message. We rely on the thread list
// payload (`/api/inbox/threads`) which carries unreadSchool + lastBody +
// lastFromRole + lastAt + subject — that's enough to render the popup
// WITHOUT marking anything read on the server. Only when the user clicks
// "OK" or "Inbox ଯିବ" do we GET the thread (which marks readBySchool=true).
//
// Modal queue: The dashboard ALSO renders OdiaMessagePopup which uses a
// custom div overlay at z-[70]. To avoid both fighting for the screen we
// (a) wait for the Odia popup's sessionStorage flag `odia_popup_seen_v1`
// before opening, and (b) force this Dialog above z-[70] via className
// overrides on overlay + content.

import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, MailOpen, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEEN_KEY_PREFIX = "mds-adminmsg-seen-";
const ODIA_FLAG = "odia_popup_seen_v1";

export default function AdminMessagePopup() {
  const navigate = useNavigate();
  const [pending, setPending] = useState(null); // { thread }
  const [open, setOpen] = useState(false);
  const firedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let pollId = null;

    const tryShow = async () => {
      if (firedRef.current) return;
      try {
        const r = await axios.get(`${API}/inbox/threads`);
        const threads = r.data || [];
        // Pick the most-recent thread that (a) has unread school messages
        // AND (b) ended with an admin message AND (c) we haven't already
        // shown for this `lastAt` timestamp. Sorted server-side by lastAt
        // desc — we take the first match.
        const target = threads.find((t) =>
          (t.unreadSchool || 0) > 0
          && t.lastFromRole === "admin"
          && (t.lastBody || "").trim()
          && !localStorage.getItem(`${SEEN_KEY_PREFIX}${t.id}-${t.lastAt}`),
        );
        if (target && !cancelled) {
          firedRef.current = true;
          setPending({ thread: target });
          setOpen(true);
        }
      } catch (e) {
        // 401 / 403 on non-school users — silently skip.
        console.debug("admin-msg-popup", e?.response?.status);
      }
    };

    // Poll for the Odia popup to be dismissed. Once its sessionStorage
    // flag is set OR ~10s have passed, fire ours. We deliberately wait
    // because OdiaMessagePopup uses a custom div overlay that visually
    // blocks the Radix Dialog underneath even though both render.
    const start = Date.now();
    pollId = setInterval(() => {
      if (cancelled || firedRef.current) {
        clearInterval(pollId);
        return;
      }
      const odiaSeen = (() => { try { return !!sessionStorage.getItem(ODIA_FLAG); } catch { return true; } })();
      const elapsed = Date.now() - start;
      if (odiaSeen || elapsed > 10000) {
        clearInterval(pollId);
        // Tiny extra delay so the Odia close animation finishes (~300ms).
        setTimeout(tryShow, 350);
      }
    }, 400);

    return () => { cancelled = true; if (pollId) clearInterval(pollId); };
  }, []);

  const markSeen = (thread) => {
    try { localStorage.setItem(`${SEEN_KEY_PREFIX}${thread.id}-${thread.lastAt}`, "1"); } catch {/* no-op */}
  };

  const onAck = async () => {
    if (!pending) { setOpen(false); return; }
    // GET-ing the thread marks all messages readBySchool=true (see
    // inbox.py /inbox/threads/{id}). We do it ONLY now — at ack time —
    // so the unread badge in Navbar clears.
    try { await axios.get(`${API}/inbox/threads/${pending.thread.id}`); } catch {/* no-op */}
    markSeen(pending.thread);
    setOpen(false);
    setPending(null);
  };

  const goToInbox = async () => {
    await onAck();
    navigate("/inbox");
  };

  const niceDate = useMemo(() => {
    if (!pending?.thread?.lastAt) return "";
    try { return new Date(pending.thread.lastAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return ""; }
  }, [pending]);

  if (!pending) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onAck(); }}>
      <DialogContent
        data-testid="admin-message-popup"
        // overlay sibling lives inside the DialogPortal — we bump BOTH via
        // className arg-merging. cn() in shadcn dialog appends after the
        // default, so z-[90] overrides the default z-50.
        className="!z-[90] w-[calc(100vw-1.5rem)] max-w-md overflow-hidden border-0 p-0 shadow-[0_30px_60px_-15px_rgb(var(--clay)/0.45)]"
      >
        {/* Eye-catching sunset gradient hero */}
        <div className="relative bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 px-6 py-7 text-white">
          <div className="absolute inset-0 opacity-25 [background:radial-gradient(circle_at_20%_-10%,white_0,transparent_60%)]" />
          <div className="relative">
            <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-1 text-[0.6rem] font-bold uppercase tracking-[0.2em] backdrop-blur">
              <Sparkles className="h-3 w-3" /> Admin Message · ବିଶେଷ ସୂଚନା
            </span>
            <DialogHeader className="mt-3">
              <DialogTitle asChild>
                <h2 className="font-heading text-2xl font-extrabold leading-tight text-white" data-testid="admin-popup-subject">
                  💌 {pending.thread.subject || "ADMIN ସୂଚନା"}
                </h2>
              </DialogTitle>
              <DialogDescription className="mt-1 text-xs text-white/95">{niceDate}</DialogDescription>
            </DialogHeader>
          </div>
        </div>

        {/* Body */}
        <div className="space-y-3 px-6 py-5">
          <p className="whitespace-pre-line text-sm leading-relaxed text-ink-900" data-testid="admin-popup-body">
            {pending.thread.lastBody}
          </p>
          <p className="rounded-lg border border-clay/20 bg-clay/5 px-3 py-2 text-[0.7rem] text-ink-700">
            <Sparkles className="mr-1 inline h-3 w-3 text-clay" />
            ଏହି ସୂଚନା ଆପଣଙ୍କ <b>Inbox</b> ରେ ସୁରକ୍ଷିତ ରଖାଯାଇଛି · ଆପଣ ସେଠାରୁ reply ମଧ୍ୟ କରିପାରିବେ।
          </p>
        </div>

        <DialogFooter className="flex flex-col-reverse gap-2 border-t border-border bg-sand-50 px-5 py-3 sm:flex-row sm:items-center sm:justify-end">
          <Button variant="outline" onClick={onAck} data-testid="admin-popup-ack">
            <MailOpen className="mr-1 h-4 w-4" /> OK · ବୁଝିଲି
          </Button>
          <Button onClick={goToInbox} data-testid="admin-popup-go-inbox" className="bg-clay text-white hover:bg-clay-dark">
            Inbox ଯିବ <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
